import { useQuery, useMutation } from "@tanstack/react-query";
import moment from "moment/moment";
import React, { useEffect, useState, useRef } from "react";
import { CiFilter } from "react-icons/ci";
import * as images from "../../assets/image";
import FilterModal from "../../common/FilterModal";
import useRole from "../../hooks/useRole";
import {
  downloadTimesheet,
  getPayouts,
  softDeletePayout,
  updatePayout,
} from "../../services/services";
import { constant } from "../../utils/constants";
import { downloadFile } from "../../utils/function";
import Pagination from "../../utils/Pagination";
import { MdOutlineFileDownload, MdDeleteOutline } from "react-icons/md";
import PayoutModal from "../../common/PayoutModal";
import { toastAlert } from "../../utils/SweetAlert";
import Swal from "sweetalert2";

const PayOut = () => {
  const [search, setSearch] = useState();
  const [page, setPage] = useState(constant.PAGE_NO_ONE);
  const [filterShow, setFilterShow] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const tableContainerRef = useRef(null);
  const role = useRole();

  const handleFilterShow = () => setFilterShow(true);

  useEffect(() => {
    setPage(1);
  }, [filterData]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      console.log("isMobile:", window.innerWidth < 768, "Window Width:", window.innerWidth);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { data, refetch } = useQuery({
    queryKey: ["payout-list", page, filterData],
    queryFn: async () => {
      let params = {
        page: page,
        limit: constant.PAGE_LIMIT,
        sortedBy: "eventDetails.startTime",
        sortBy: constant.SORT_BY.DESC,
      };
      if(role==="client"){
        params.needEmployeeDetails=true
      }
      if (
        Object.keys(filterData).length &&
        Object.keys(filterData).filter((item) => filterData[item]).length
      ) {
        params.range = `${filterData.start}TO${filterData.end}`;
      }
      if (search) params.search = search;
      const resp = await getPayouts(params);
      return resp?.data?.data || {};
    },
  });

  const handleSampleDownload = async () => {
    try {
      let params = {};
      if (
        Object.keys(filterData).length &&
        Object.keys(filterData).filter((item) => filterData[item]).length
      ) {
        params.range = `${filterData.start}TO${filterData.end}`;
      }
      if (search) params.search = search;
      let resp = await downloadTimesheet(params);
      let fileName = params?.range
        ? `${params?.range}_timesheet.xlsx`
        : "timesheet.xlsx";
      downloadFile(resp?.data, fileName);
    } catch (err) {
      console.log("err", err);
    }
  };

  const handleEditClick = (payout) => {
    setSelectedPayout(payout);
    setShowEditModal(true);
  };

  const updateMutation = useMutation({
    mutationFn: ({ id, body }) => updatePayout(id, body),
    onSuccess: (resp) => {
      toastAlert("success", resp?.data?.message);
      refetch();
    },
  });

  const toggleSelection = (payoutId) => {
    setSelected((prev) =>
      prev.includes(payoutId)
        ? prev.filter((id) => id !== payoutId)
        : [...prev, payoutId]
    );
  };

  const selectAll = (event) => {
    const { target: { checked } } = event;
    setSelected(checked ? data.payouts.map((pay) => pay._id) || [] : []);
  };

  const handleSoftDelete = () => {
    if (!selected.length) {
      toastAlert("error", "No payout has been selected");
      return;
    }
    Swal.fire({
      title: "Are you sure?",
      text: `You are going to delete ${selected.length} events`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d1227",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    }).then((result) => {
      if (result.isConfirmed) {
        if (selected.length) {
          let body = { payoutIds: selected };
          softDeleteMutation.mutate(body);
        }
      }
    });
  };

  const softDeleteMutation = useMutation({
    mutationFn: (body) => softDeletePayout(body),
    onSuccess: (resp) => {
      toastAlert("success", resp?.data?.message);
      refetch();
    },
  });

  return (
    <section className="main-content">
      <div className="commonSearchBar mt-3">
        <h4 className="heading20">Payout</h4>
        <div className="endContent">
          <div className="clientSearch">
            <img
              src={images.Search}
              alt="searchImg"
              className="clientSearchIcon"
            />
            <input
              type="text"
              placeholder="Type and press enter to search."
              className="clientSearchInput"
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              onKeyDown={(e) =>
                e.key === "Enter" && search?.trim() !== "" && refetch()
              }
              onKeyUp={(e) =>
                e.target.value === "" && e.key === "Backspace" && refetch()
              }
            />
          </div>
          {role === constant.ROLE.ADMIN && (
            <>
              <div className="position-relative">
                {Object.keys(filterData).length &&
                Object.keys(filterData).filter((item) => filterData[item]).length ? (
                  <div className="redDot" />
                ) : (
                  ""
                )}
                <button
                  className="greenBtn calendar-btn"
                  type="button"
                  title="Filter By"
                  onClick={handleFilterShow}
                >
                  <CiFilter size={35} />
                </button>
              </div>
              <button
                className="greenBtn calendar-btn"
                type="button"
                title="Delete Selected"
                onClick={handleSoftDelete}
              >
                <MdDeleteOutline size={30} />
              </button>
              <button
                className="greenBtn calendar-btn"
                type="button"
                title="Auto Assign"
                onClick={handleSampleDownload}
              >
                <MdOutlineFileDownload size={30} />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="container-fluid p-0">
        <div className="row mt-4">
          <div className="col-md-12">
            <div
              ref={tableContainerRef}
              style={{
                overflowX: isMobile ? "auto" : "visible",
                WebkitOverflowScrolling: "touch",
                width: "100%",
                position: "relative",
                paddingBottom: isMobile ? "20px" : "0",
              }}
            >
              {isMobile && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "0",
                    left: "50%",
                    transform: "translateX(-50%)",
                    color: "#666",
                    fontSize: "12px",
                  }}
                >
                  ← Scroll →
                </div>
              )}
              <table
                className="responsive_table"
                style={{
                  width: "100%",
                  minWidth: isMobile ? "900px" : "100%",
                  borderCollapse: "separate",
                  borderSpacing: "0",
                }}
              >
                <thead style={{  backgroundColor: "#f8f9fa" }}>
                  <tr>
                    {role === constant.ROLE.ADMIN && (
                      <th
                        className="tableHeading"
                        style={{
                          width: "40px",
                          minWidth: "40px",
                          borderRadius: "10px 0px 0px 0px",
                          position: isMobile ? "sticky" : "static",
                          left: isMobile ? "0" : "auto",
                          zIndex: isMobile ? "9" : "auto",
                          backgroundColor: isMobile ? "#f8f9fa" : "transparent",
                        }}
                      >
                        <input type="checkbox" onChange={selectAll} />
                      </th>
                    )}
                    {role !== constant.ROLE.EMPLOYEE && (
                      <th
                        className="tableHeading"
                        style={{
                          minWidth: isMobile ? "80px" : "120px",
                          borderRadius: role === constant.ROLE.ADMIN ? "0" : "10px 0px 0px 0px",
                          position: isMobile ? "sticky" : "static",
                          left: isMobile ? (role === constant.ROLE.ADMIN ? "40px" : "0") : "auto",
                          zIndex: isMobile ? "8" : "auto",
                          backgroundColor: isMobile ? "#f8f9fa" : "transparent",
                        }}
                      >
                        Employee Name
                      </th>
                    )}
                  {role!==constant.ROLE.CLIENT&&<th
                      className="tableHeading"
                      style={{
                        minWidth: isMobile ? "80px" : "120px",
                        position: isMobile ? "sticky" : "static",
                        left: isMobile ? (role === constant.ROLE.ADMIN ? (role !== constant.ROLE.EMPLOYEE ? "120px" : "40px") : (role !== constant.ROLE.EMPLOYEE ? "67px" : "0")) : "auto",
                        zIndex: isMobile ? "8" : "auto",
                        backgroundColor: isMobile ? "#f8f9fa" : "transparent",
                      }}
                    >
                      Client Name
                    </th>}
                    <th
                      className="tableHeading"
                      style={{
                        minWidth: isMobile ? "100px" : "120px",
                        position: isMobile ? "sticky" : "static",
                        left: isMobile ? (role === constant.ROLE.ADMIN ? (role !== constant.ROLE.EMPLOYEE ? "200px" : "120px") : (role !== constant.ROLE.EMPLOYEE ? "77px" : "48px")) : "auto",
                        zIndex: isMobile ? "8" : "auto",
                        backgroundColor: isMobile ? "#f8f9fa" : "transparent",
                      }}
                    >
                      Assignment Date
                    </th>
                    <th
                      className="tableHeading"
                      style={{
                        minWidth: "120px",
                      }}
                    >
                      Check-In
                    </th>
                    <th
                      className="tableHeading"
                      style={{
                        minWidth: "120px",
                      }}
                    >
                      Check-Out
                    </th>
                    <th
                      className="tableHeading"
                      style={{
                        minWidth: "120px",
                      }}
                    >
                      Actual Check-In
                    </th>
                    <th
                      className="tableHeading"
                      style={{
                        minWidth: "120px",
                      }}
                    >
                      Actual Check-Out
                    </th>
                    <th
                      className="tableHeading"
                      style={{
                        minWidth: "120px",
                      }}
                    >
                      Client Initials
                    </th>
                    <th
                      className="tableHeading"
                      style={{
                        minWidth: "120px",
                        borderRadius: "0px 10px 0px 0px",
                      }}
                    >
                      Note
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data && data.payouts && data?.payouts?.length > 0 ? (
                    data?.payouts?.map((payout, index) => {
                      const employeeDetails = payout?.employeeDetails || null;
                      const eventDetails = payout?.eventDetails || null;
                      return (
                        <tr key={`payout_${index}`}>
                          {role === constant.ROLE.ADMIN && (
                            <td
                              style={{
                                width: "40px",
                                minWidth: "40px",
                                position: isMobile ? "sticky" : "static",
                                left: isMobile ? "0" : "auto",
                                zIndex: isMobile ? "9" : "auto",
                                backgroundColor: isMobile ? "white" : "transparent",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selected.includes(payout._id)}
                                onChange={() => toggleSelection(payout._id)}
                              />
                            </td>
                          )}
                          {role !== constant.ROLE.EMPLOYEE && (
                            <td
                              className="text-capitalize"
                              style={{
                                minWidth: isMobile ? "80px" : "120px",
                                position: isMobile ? "sticky" : "static",
                                left: isMobile ? (role === constant.ROLE.ADMIN ? "40px" : "0") : "auto",
                                zIndex: isMobile ? "8" : "auto",
                                backgroundColor: isMobile ? "white" : "transparent",
                              }}
                            >
                              {employeeDetails
                                ? `${employeeDetails?.firstName || ""} ${employeeDetails?.lastName || ""}`
                                : "Employee not available"}
                            </td>
                          )}
                        {role!==constant.ROLE.CLIENT&&  <td
                            className="text-capitalize"
                            style={{
                              minWidth: isMobile ? "80px" : "120px",
                              position: isMobile ? "sticky" : "static",
                              left: isMobile ? (role === constant.ROLE.ADMIN ? (role !== constant.ROLE.EMPLOYEE ? "120px" : "40px") : (role !== constant.ROLE.EMPLOYEE ? "67px" : "0")) : "auto",
                              zIndex: isMobile ? "8" : "auto",
                              backgroundColor: isMobile ? "white" : "transparent",
                            }}
                          >
                            {payout?.clientDetails
                              ? `${payout?.clientDetails?.firstName || ""} ${payout?.clientDetails?.lastName || ""}`
                              : "Client not available"}
                          </td>}
                          <td
                            style={{
                              minWidth: isMobile ? "100px" : "120px",
                              position: isMobile ? "sticky" : "static",
                              left: isMobile ? (role === constant.ROLE.ADMIN ? (role == constant.ROLE.EMPLOYEE ? "200px" : "120px") : (role !== constant.ROLE.EMPLOYEE ? "77px" : "48px")) : "auto",
                              zIndex: isMobile ? "8" : "auto",
                              backgroundColor: isMobile ? "white" : "transparent",
                            }}
                          >
                            {eventDetails?.startTime
                              ? moment(eventDetails?.startTime).format("ll")
                              : "N/A"}
                          </td>
                          <td
                            style={{
                              minWidth: "120px",
                            }}
                          >
                            {eventDetails?.startTime
                              ? moment(eventDetails.startTime).format("LT")
                              : "N/A"}
                          </td>
                          <td
                            style={{
                              minWidth: "120px",
                            }}
                          >
                            {eventDetails?.endTime
                              ? moment(eventDetails.endTime).format("LT")
                              : "N/A"}
                          </td>
                          <td
                            style={{
                              minWidth: "120px",
                            }}
                          >
                            {payout
                              ? moment(payout.checkIn, "HH:mm").format("hh:mm A")
                              : "N/A"}
                          </td>
                          <td
                            style={{
                              minWidth: "120px",
                            }}
                          >
                            {payout
                              ? moment(payout.checkOut, "HH:mm").format("hh:mm A")
                              : "N/A"}
                          </td>
                          <td
                            className="sign-area"
                            style={{
                              minWidth: "120px",
                            }}
                          >
                            {payout?.signature ? (
                              <img
                                src={payout?.signature}
                                className="digital-sign"
                                alt="Signature"
                              />
                            ) : (
                              "N/A"
                            )}
                          </td>
                          <td
                            style={{
                              minWidth: "120px",
                            }}
                          >
                            {payout && payout.note && payout?.note?.trim()
                              ? payout?.note.slice(0, 30)
                              : "N/A"}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        style={{ textAlign: "center" }}
                        colSpan={role === constant.ROLE.ADMIN ? 10 : 9}
                      >
                        No Data Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <Pagination
                page={page}
                setPage={setPage}
                totalPages={data?.total_pages}
              />
            </div>
          </div>
        </div>
      </div>
      {showEditModal && (
        <PayoutModal
          show={showEditModal}
          onClose={() => setShowEditModal(false)}
          payoutData={selectedPayout}
          onSave={(updatedPayout) => {
            updateMutation.mutate({
              id: selectedPayout?._id,
              body: updatedPayout,
            });
            setShowEditModal(false);
          }}
        />
      )}
      {filterShow && (
        <FilterModal
          show={filterShow}
          setShow={setFilterShow}
          refetch={refetch}
          data={filterData}
          setData={setFilterData}
        />
      )}
    </section>
  );
};

export default PayOut;